<script>
import moment from 'moment/moment';
import RejectRequestModal from '@/components/socialVisits/RejectRequestModal.vue';
import SocialVisitsService from '@/services/SocialVisitsService';
import CancelVisitModal from '@/components/socialVisits/CancelVisitModal.vue';
import IdentityType from '@/components/sharedVisitorDetails/IdentityType.vue';
import VisitorImages from '@/components/visitors/details/sections/VisitorImages.vue';
import AdditionalVisitors from '@/components/socialVisits/AdditionalVisitors.vue';
import { mapState } from 'vuex';
import VisitorSocialBans from '@/views/socialBans/VisitorSocialBans.vue';
import RefundSocialKioskItemsModal from '@/components/modals/RefundSocialKioskItemsModal.vue';

export default {
  name: 'SocialVisitDetails',
  components: {
    RefundSocialKioskItemsModal,
    VisitorSocialBans,
    VisitorImages,
    IdentityType,
    CancelVisitModal,
    RejectRequestModal,
    AdditionalVisitors,
  },
  data() {
    return {
      loading: true,
      visitor: null,
      additionalVisitors: [],
      additionalVisitorFields: [
        { label: 'Name', key: 'name' },
        { label: 'Date of birth', key: 'dob' },
      ],
      rejectModalId: 'reject-modal',
      cancelModalId: 'cancel-modal',
      refundModalId: 'refund-kiosk-social-modal',
    };
  },
  computed: {
    ...mapState({
      visit: state => state.socialVisits.visit,
      visitorsFirstVisit: state => state.socialVisits.visitorsFirstVisit,
    }),
    showFirstVisitAlert() {
      const allowedStatuses = ['Requested', 'AwaitingConfirmation', 'Approved'];
      return allowedStatuses.includes(this.visit.status) && this.visitorsFirstVisit;
    },
    showRefundKioskItemsButtons() {
      if (this.visit.status !== 'Completed') {
        return false;
      }

      const refundAlreadyProcessed = this.visit.kiosk_items.some(item => {
        const refunded = item.amount_refunded === null ? 0 : item.amount_refunded;
        return refunded > 0;
      });

      return !refundAlreadyProcessed;
    },
    hasRefundedKioskItems() {
      return this.visit.kiosk_items.some(item => item.amount_refunded > 0);
    },
  },
  async mounted() {
    try {
      if (!this.visit) {
        await this.getVisit();
      }
      await Promise.all([
        this.getVisitorDetails(),
        this.getAdditionalVisitors(),
        this.getPreviousVisits(),
      ]);
      this.loading = false;
    } catch (e) {
      this.$router.go(-1);
    }
  },
  methods: {
    async getVisitorDetails() {
      try {
        await this.$store.dispatch('visitor/getVisitor', this.visit.visitor.user_id);
        this.visitor = this.$store.state.visitor.visitor;
      } catch (e) {
        console.error('Error getting visitor details: ', e);
        this.$toast.error('Error getting visitor details', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    async getAdditionalVisitors() {
      try {
        await this.$store.dispatch('socialVisits/getAdditionalVisitors', this.visit._id);
        this.additionalVisitors = this.$store.state.socialVisits.additionalVisitors;
      } catch (e) {
        console.error('Error getting additional visitors: ', e);
        this.$toast.error('Error getting additional visitors', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    async getPreviousVisits() {
      try {
        const { data } = await SocialVisitsService.getPreviousSocialVisits(this.visit.visitor.user_id);
        this.$store.commit('socialVisits/SET_VISITORS_FIRST_VISIT', !data.length);
      } catch (e) {
        console.error('Error getting previous visits: ', e);
        throw e;
      }
    },
    async getVisit() {
      try {
        const response = await SocialVisitsService.getVisit(this.$route.params.id);
        await this.$store.dispatch('socialVisits/setVisit', response.data);
      } catch (e) {
        console.error('Error getting social visit: ', e);
        this.$toast.error('Error getting social visit', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    formatDobAndAge(dob) {
      return `${moment(dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(dob, 'years')})`;
    },
    showModal(modalId) {
      this.$bvModal.show(modalId);
    },
    closeModals() {
      this.$bvModal.hide(this.rejectModalId);
      this.$bvModal.hide(this.cancelModalId);
      this.$bvModal.hide(this.refundModalId);
    },
    refresh() {
      this.closeModals();
      this.getVisit();
      this.getAdditionalVisitors();
    },
    getVariant() {
      switch (this.visit.status) {
        case 'Requested':
          return 'info';
        case 'Rejected':
          return 'danger';
        case 'Completed':
          return 'success';
        case 'Cancelled':
          return 'warning';
        default:
          return 'primary';
      }
    },
    async approveRequest() {
      try {
        this.loading = true;
        await SocialVisitsService.approveRequest(this.visit._id, {
          desired_booking_slot_id: this.visit.booked_slot.booking_slot_id,
        });
        await this.getVisit();
        this.$toast.success('Request approved');
      } catch (e) {
        console.error('Error approving request: ', e);
        this.$toast.error('Error approving request');
      } finally {
        this.loading = false;
      }
    },
    canCancelVisit() {
      const { status } = this.visit;
      return status !== 'Cancelled' && status !== 'Completed' && status !== 'Requested' && status !== 'Rejected';
    },
    getItemTotal(item) {
      return item.amount * item.price;
    },
    getTotalOfKioskItems() {
      if(this.visit.kiosk_items && this.visit.kiosk_items.length > 0) {
        let total = 0;
        this.visit.kiosk_items.forEach((kioskItem) => {
          total += (kioskItem.amount * kioskItem.price);
        });
        return total.toFixed(2);
      }
      return '0';
    },
    showRefundModal() {
      const visitDate = new Date(this.visit.booked_slot.start_date_time);
      const currentDate = new Date();
      if (visitDate.getMonth() !== currentDate.getMonth() || visitDate.getFullYear() !== currentDate.getFullYear()) {
        this.$swal({
          title: 'Unable to refund',
          text: 'This visit is not in the current month/year. Please contact staff at Made Purple in order to process a refund.',
          icon: 'info',
          buttons: true,
        });
        return;
      }

      this.showModal(this.refundModalId);
    },
  },
}
</script>

<template>
  <div v-if="loading">
    <div class="col text-center mt-5">
      <span class="fas fa-spinner fa-spin" style="font-size: 2em;" />
    </div>
  </div>
  <div v-else>
    <h2 class="font-weight-bolder mb-2 full-width">Social Visit Details</h2>
    <router-link :to="{ name: 'social-visits-home' }" class="d-inline-block mb-2">
      &laquo; Back to social visits
    </router-link>
    <b-row>
      <b-col cols="6">
        <!--Visit Overview-->
        <b-card>
          <b-card-title>
            Visit Overview
            <b-badge v-if="visit.booked_by" variant="secondary" class="float-right">
              {{ visit.booked_by }} requested
            </b-badge>
          </b-card-title>

          <b-alert v-if="showFirstVisitAlert" class="p-1" show variant="danger">
            This is the visitor's first social visit
          </b-alert>

          <p>
            <b>{{ visit.visitor.firstname }} {{ visit.visitor.lastname }}</b>
            has requested a visit with
            <b>{{ visit.prisoner.firstname }} {{ visit.prisoner.lastname }} ({{ visit.prisoner.prisoner_number }})</b>
            on the
            <b>{{ visit.booked_slot.start_date_time | formatDate }}</b>
            for the slot beginning at
            <b>{{ visit.booked_slot.start_date_time | formatTime }}</b>
            and ending at
            <b>{{ visit.booked_slot.end_date_time | formatTime }}</b>.
          </p>

          <div v-if="(visit.status === 'Rejected' || visit.status === 'Cancelled') && visit.cancellation_reason">
            <strong>
              This visit was {{ visit.status.toLowerCase() }} because:
            </strong>
            <span>{{ visit.cancellation_reason }}</span>
          </div>

          <div class="d-flex justify-content-end">
            <div v-if="visit.status === 'Requested'">
              <b-btn :disabled="loading" class="mr-1" variant="success" @click="approveRequest">
                <span class="fas fa-check" /> Approve Request
              </b-btn>
              <b-btn variant="danger" :disabled="loading" @click="showModal(rejectModalId)">
                Reject
              </b-btn>
            </div>
            <div v-if="canCancelVisit()" class="ml-1">
              <b-btn variant="warning" :disabled="loading" @click="showModal(cancelModalId)">
                Cancel Visit
              </b-btn>
            </div>
          </div>
        </b-card>

        <!--Visit Details-->
        <b-card>
          <b-card-title>
            Visit Details
          </b-card-title>
          <div class="detail-item">
            Status:
            <b-badge size="sm" :variant="getVariant()">
              {{ visit.status }}
            </b-badge>
          </div>
          <div v-if="visit.status === 'Cancelled'" class="detail-item">
            {{ visit.status }} by:
            <b>{{ visit.cancelled_by }}</b>
          </div>
          <div class="detail-item">
            Location:
            <b>{{ visit.visit_location.name }}</b>
          </div>
          <div class="detail-item">
            Requested on:
            <b>{{ visit.created_at | formatDateTime }}</b>
          </div>
          <div class="detail-item">
            Booked slot:
            <b>{{ visit.booked_slot.start_date_time | formatDateTime }} - {{
              visit.booked_slot.end_date_time | formatTime
            }}</b>
          </div>
          <div class="detail-item">
            Resident name:
            <b>{{ visit.prisoner.firstname }} {{ visit.prisoner.lastname }}</b>
          </div>
          <div class="detail-item">
            Resident ID:
            <b>{{ visit.prisoner.prisoner_number }}</b>
          </div>
          <div class="detail-item">
            Relationship to resident:
            <b>{{ visit.prisoner.relationship_to_booker }}</b>
          </div>
        </b-card>

        <!--Additional Visitors-->
        <additional-visitors
          :show-add-btn="true"
          :show-remove-btn="true"
          @refresh="refresh"
        />

        <!--Kiosk Items-->
        <b-card v-if="visit.kiosk_items && visit.kiosk_items.length > 0">
          <b-card-title class="d-flex align-items-center">
            Kiosk Items
            <div class="ui-spacer" />
            <b-btn
              v-if="showRefundKioskItemsButtons"
              size="sm"
              variant="warning"
              @click="showRefundModal"
            >
              Refund items
            </b-btn>
          </b-card-title>
          <section class="mb-2">
            <div v-for="item in visit.kiosk_items" :key="item._id" class="kiosk-item-row d-flex justify-content-between">
              <p class="mb-0">{{item.name}}</p>
              <p class="mb-0">
                <span class="badge badge-success badge-pill mr5">{{item.amount}} x {{$options.filters.regionCurrency()}}{{item.price.toFixed(2)}}</span>
                <span class="badge badge-primary badge-pill">Total: {{$options.filters.regionCurrency()}}{{(item.amount * item.price).toFixed(2)}}</span>
                <span v-if="item.amount_refunded > 0" class="badge badge-warning badge-pill ml-1">
                  Refunded: {{ item.amount_refunded }} items ({{$options.filters.regionCurrency()}}{{(item.amount_refunded * item.price).toFixed(2)}})
                </span>
              </p>
            </div>
          </section>
          <section>
            <p class="detail-item mb-0" cy="kiosk-total">Kiosk Total: <b>{{$options.filters.regionCurrency()}}{{getTotalOfKioskItems()}}</b></p>
          </section>
        </b-card>

        <!--Social Bans-->
        <visitor-social-bans :visitor_user_id="visit.visitor.user_id" />
      </b-col>

      <!--Visitor Details-->
      <b-col cols="6">
        <b-card>
          <b-card-title>
            Visitor Details
          </b-card-title>

          <!--ID Section-->
          <visitor-images />
          <identity-type :person="visitor" />

          <!--Basic Info-->
          <div class="detail-item">
            Name:
            <b>{{ visit.visitor.firstname }} {{ visit.visitor.lastname }}</b>
          </div>
          <div class="detail-item">
            Email:
            <b>{{ visit.visitor.email }}</b>
          </div>
          <div class="detail-item">
            Phone number:
            <b>{{ visit.visitor.phone_number }}</b>
          </div>
          <div class="detail-item">
            D.O.B. & age:
            <b>{{ formatDobAndAge(visitor.dob) }}</b>
          </div>
          <div class="detail-item">
            Address line 1:
            <b>{{ visitor.address.address1 }}</b>
          </div>
          <div class="detail-item">
            Address line 2:
            <b>{{ visitor.address.address2 }}</b>
          </div>
          <div v-if="visitor.address.address3" class="detail-item">
            Address line 3:
            <b>{{ visitor.address.address3 }}</b>
          </div>
          <div class="detail-item">
            City/Town:
            <b>{{ visitor.address.city_town }}</b>
          </div>
          <div class="detail-item">
            Region:
            <b>{{ visitor.address.region }}</b>
          </div>
          <div class="detail-item">
            Postcode:
            <b>{{ visitor.address.zip_postal_code }}</b>
          </div>
          <div class="detail-item">
            Country code:
            <b>{{ visitor.address.country }}</b>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-modal :id="rejectModalId" hide-footer>
      <reject-request-modal :visit="visit" @close="closeModals" @refresh="refresh" />
    </b-modal>

    <b-modal :id="cancelModalId" hide-footer>
      <cancel-visit-modal :visit="visit" @close="closeModals" @refresh="refresh" />
    </b-modal>

    <refund-social-kiosk-items-modal
      :visit-id="visit._id"
      :kiosk-items="visit.kiosk_items"
      @close="closeModals"
      @refresh="refresh"
    />
  </div>
</template>

<style scoped>
.detail-item {
  display: flex;
  justify-content: space-between;
}

.kiosk-item-row {
  border: 2px solid #ebe9f1; /* Increased border thickness */
  border-left: none;
  border-right: none;
  padding: 10px 0;

  &:nth-child(2) {
    border-top: none;
  }
}
</style>
